import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useSubjectsDataGridFilter } from '../../../../../common/DataGrid/filters/DataGridFilters.jsx';
import { useDataGridTextFilter } from '../../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import {
  dataGridDateSorter,
  dataGridTextSorter,
} from '../../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import DataGrid from '../../../../../common/DataGrid/DataGrid.jsx';
import { Spinner } from '../../../../../common/index.jsx';
import { FileSearchOutlined } from '@ant-design/icons';
import { IconButton } from '../../../../../common/controls/Controls.jsx';
import dayjs from 'dayjs';

const AttendanceEventsTable = ({
  attendanceEvents,
  subjects,
  onDetailClick,
  onDelete,
}) => {
  const { t } = useTranslation();
  const subjectsFilterProps = useSubjectsDataGridFilter('subject_id', subjects);
  const titleTextFilterProps = useDataGridTextFilter('title');

  const columns = useMemo(
    () => [
      {
        title: t('Názov'),
        dataIndex: 'title',
        ellipsis: true,
        render: (title) => title ?? t('Žiadny'),
        sorter: dataGridTextSorter('title'),
        ...titleTextFilterProps,
      },
      {
        title: t('Predmet'),
        dataIndex: 'subject_id',
        ellipsis: true,
        ...subjectsFilterProps, // includes render
      },
      {
        title: t('Prítomný učiteľ'),
        dataIndex: 'created_by',
        ellipsis: true,
        render: (person) => (person ? `${person.full_name}` : t('Žiadny')),
        sorter: dataGridTextSorter('created_by.last_name'),
        ...titleTextFilterProps,
      },
      {
        title: t('Zastupujúci učiteľ'),
        dataIndex: 'teacher_id',
        ellipsis: true,
        render: (person) => (person ? `${person.full_name}` : t('Žiadny')),
        sorter: dataGridTextSorter('teacher_id.last_name'),
        ...titleTextFilterProps,
      },
      {
        title: t('Dátum'),
        dataIndex: 'started_at',
        ellipsis: true,
        render: (started) => dayjs(started).format('DD.MM.YYYY'),
        sorter: dataGridDateSorter('started_at'),
        defaultSortOrder: 'ascend',
        ...titleTextFilterProps,
      },
      {
        title: t('Čas'),
        dataIndex: 'started_at',
        ellipsis: true,
        render: (_, record) => {
          return `${dayjs(record.started_at).format('HH:mm')} - ${dayjs(record.ended_at).format('HH:mm')}`;
        },
        ...titleTextFilterProps,
      },
    ],
    [t, titleTextFilterProps],
  );

  if (!attendanceEvents) {
    return <Spinner />;
  }

  const customSettings = (row) => {
    return (
      <div style={{ display: 'flex' }}>
        <IconButton
          icon={<FileSearchOutlined />}
          tooltip={t('Zobraziť detail udalosti')}
          onClick={() => onDetailClick(row)}
        />
      </div>
    );
  };

  return (
    <DataGrid
      rowKey={(event) => event.id}
      columns={columns}
      dataSource={attendanceEvents || []}
      permissionsBasic={'attendance.show'}
      sorter={dataGridDateSorter('started_at')}
      customSettings={customSettings}
      onDelete={onDelete}
      onRowDetailClick={onDetailClick}
    />
  );
};

AttendanceEventsTable.propTypes = {
  attendanceEvents: PropTypes.array,
  onDelete: PropTypes.func,
  onDetailClick: PropTypes.func,
};

export default AttendanceEventsTable;
